import { Control, Controller, FieldPath, FieldValues } from "react-hook-form";

import { CodeVerification } from "~/common/components";
import SignUpFormLayout from "~/common/components/signUp/SignUpFormLayout";
import SignUpProgress from "~/common/components/signUp/SignUpProgress";
import { useCodeVerificationCooldown } from "~/common/hooks/useCodeVerificationCooldown";
import { useOnBoardingNewPatientStore } from "~/patients/stores/useOnBoardingNewPatientStore";
import { UserMobileVerificationSchemaType } from "~/patients/utils";
import { FormErrors } from "~/patients/utils/types/forms";

interface PatientMobileVerificationFormProps<T extends FieldValues> {
  handleGoBack: () => void;
  totalSteps: number;
  control?: Control<T>;
  onNext: () => Promise<void>;
  onRetry?: () => void;
  canResendAt: number;
  step: number;
  errors?: FormErrors<UserMobileVerificationSchemaType>;
  codeFieldKey: FieldPath<T>;
  pendingRequest?: boolean;
}

export const PatientMobileVerificationForm = <T extends FieldValues>({
  onNext,
  onRetry,
  control,
  handleGoBack,
  totalSteps,
  errors,
  step,
  codeFieldKey,
  canResendAt,
  pendingRequest,
}: PatientMobileVerificationFormProps<T>) => {
  const newPatient = useOnBoardingNewPatientStore((state) => state.newPatient);
  const { cooldown } = useCodeVerificationCooldown(canResendAt);

  return (
    <>
      <SignUpProgress step={step} totalSteps={totalSteps} />
      <SignUpFormLayout
        title="Verification"
        description={`Enter the code sent by SMS to ${newPatient?.phone_number?.phone_number}`}
        handleGoBack={handleGoBack}
        handleData={onNext}
        pendingRequest={pendingRequest}
      >
        <Controller
          name={codeFieldKey}
          control={control}
          render={({ field }) => {
            return (
              <CodeVerification
                value={field.value}
                onChange={field.onChange}
                onRetry={onRetry}
                error={errors?.code?.message}
                inCooldownTime={cooldown.active}
                remainingTime={cooldown.remainingTime}
                disabled={pendingRequest}
              />
            );
          }}
        />
      </SignUpFormLayout>
    </>
  );
};
